/**
 * Where the camera stands when the Trip Player arrives at a POI.
 *
 * Pure policy: no DOM, no Mapbox import, no clock. It takes the playback
 * sequence and an index into it and returns a camera. That is what lets
 * `camera.test.ts` cover the whole of it — including the sun, which is
 * otherwise a thing you can only check by looking at it.
 *
 * This module was built on the idea that **the photographer already answered
 * the hard question** — `pois.dir` carries the heading the phone recorded, for
 * 53 of the 54 photos in the corpus, and facing that way would make the map
 * frame and the photo frame agree. Measurement killed it. The headings are
 * present, precise-looking, and wrong: see `poiBearing` for the numbers. What
 * survives is the weaker but checkable fact that a trip has a *direction*, and
 * that arriving at a POI looking the way the trip is going is most of the
 * "following along" feeling anyway.
 */
import bearing from "@turf/bearing"
import { point } from "@turf/helpers"
import type { MapMultiLineString, MapPoi } from "./types.ts"

const DEG = Math.PI / 180

/** Mapbox Standard's `lightPreset` values, in the order the sun visits them. */
export type LightPreset = "dawn" | "day" | "dusk" | "night"

/** A camera, in the terms `flyTo` takes. */
export interface PoiCamera {
  /** Degrees clockwise from north, or `null` to hold whatever we are at. */
  bearing: number | null
  pitch: number
  zoom: number
  light: LightPreset | null
}

/**
 * How far the camera tips off vertical.
 *
 * The single most consequential number here. At 0 the app renders a diagram —
 * and pays for terrain it never shows, since `mapbox-dem` has been loaded at
 * `exaggeration: 1.5` the whole time and is invisible looking straight down.
 * 60 is high enough to put a horizon in frame and low enough that the
 * foreground does not collapse into a smear.
 */
export const PLAYER_PITCH = 60

/**
 * The zoom a photo from the main lens gets, and the anchor the rest key off.
 *
 * 14 is what `flyTo` has always defaulted to, so a typical POI frames exactly
 * as it did before this change.
 */
export const BASE_ZOOM = 14

/** The 35mm-equivalent focal length `BASE_ZOOM` corresponds to. */
export const BASE_F35 = 25

/** How far the focal-length rule is allowed to move the zoom, in either direction. */
export const ZOOM_SPAN = 1.5

/**
 * Zoom from the lens the photo was taken with.
 *
 * A telephoto shot is of something far away, so the map tightens; an ultrawide
 * sweep is of everything at once, so it pulls back. This is a taste mapping,
 * not optics — the honest version needs the subject distance, which no EXIF
 * tag records — but the corpus spans 14/21/25/27/51mm, so there is real signal
 * to spend rather than a constant dressed up as a calculation.
 *
 * Clamped, because a fisheye or a 600mm would otherwise argue the camera into
 * orbit or into the dirt.
 */
export function zoomForFocalLength(f35: number | null | undefined): number {
  if (typeof f35 !== "number" || !Number.isFinite(f35) || f35 <= 0) return BASE_ZOOM
  const offset = Math.log2(f35 / BASE_F35)
  return BASE_ZOOM + Math.max(-ZOOM_SPAN, Math.min(ZOOM_SPAN, offset))
}

/** `numeric` arrives from Postgres as a string through JSON. */
function num(v: number | string | null | undefined): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function coords(poi: MapPoi | null | undefined): [number, number] | null {
  if (!poi) return null
  const lat = num(poi.lat)
  const lng = num(poi.lng)
  return lat === null || lng === null ? null : [lng, lat]
}

/** Degrees, normalised into `[0, 360)`. */
function wrap360(deg: number): number {
  return ((deg % 360) + 360) % 360
}

/**
 * How far away a POI has to be before it counts as somewhere you are going.
 *
 * The number that makes the difference between a heading and a twitch. Two
 * photos taken twenty metres apart at a trailhead have a bearing between them,
 * but it is the bearing of a few paces, not of the walk — and pointing the
 * camera down it frames the car park instead of the mountain. 200 m is roughly
 * the scale at which consecutive POIs on a hike start describing the route
 * rather than the standing-around.
 */
export const LOOKAHEAD_METRES = 200

/** Great-circle distance in metres. Haversine; no turf dependency needed. */
function metresBetween(a: [number, number], b: [number, number]): number {
  const R = 6_371_000
  const lat1 = a[1] * DEG
  const lat2 = b[1] * DEG
  const dLat = lat2 - lat1
  const dLng = (b[0] - a[0]) * DEG
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * The direction the trip is travelling through `index`, ignoring POIs closer
 * than `minMetres`.
 *
 * Looks forward first — arriving somewhere looking onward is the whole point —
 * and only then backward, which is all the last POI of a trip has and which
 * keeps it facing the way it came rather than snapping somewhere new.
 */
function travelBearing(
  pois: MapPoi[],
  index: number,
  here: [number, number],
  minMetres: number,
): number | null {
  for (let i = index + 1; i < pois.length; i++) {
    const there = coords(pois[i])
    if (!there) continue
    const d = metresBetween(here, there)
    if (d > 0 && d >= minMetres) return wrap360(bearing(point(here), point(there)))
  }
  for (let i = index - 1; i >= 0; i--) {
    const there = coords(pois[i])
    if (!there) continue
    const d = metresBetween(there, here)
    if (d > 0 && d >= minMetres) return wrap360(bearing(point(there), point(here)))
  }
  return null
}

/**
 * How far a POI may sit from the track and still be described by it.
 *
 * A GPS fix wanders, a photo is taken a few steps off the trail, and a POI
 * dropped by hand lands wherever it lands. Past this the route is no longer
 * talking about this POI — a summit shot pinned in the wrong valley must not
 * inherit the tangent of a trail it was never on.
 */
export const ROUTE_ATTACH_METRES = 120

/**
 * How far along the track to look when reading its direction.
 *
 * Not one segment. The Picacho Peak track carries 1,373 vertices over about
 * two kilometres — roughly a metre and a half apart — so a single segment is
 * the bearing of one GPS sample against the next, which is jitter with a
 * compass rose drawn on it.
 *
 * The window is the same distance as `LOOKAHEAD_METRES`, and for the same
 * reason: below it you are reading the standing-around rather than the walk.
 * A switchbacked trail makes that choice load-bearing — measured on the
 * corpus track, the tangent at one POI swings 238 degrees between a 30 m and a
 * 60 m window, because at that scale the "direction of the trail" is whichever
 * limb of a hairpin you happened to land on. Reading across a distance that
 * spans several switchbacks answers the question the camera is actually
 * asking, which is which way the walk is going, not which way this step faces.
 */
export const ROUTE_TANGENT_METRES = LOOKAHEAD_METRES

/**
 * Where the track is heading as it passes a point, or `null` if it never does.
 *
 * The dense version of the POI-to-POI rule below, and strictly better wherever
 * it applies: POIs sample the walk a handful of times across a whole trip,
 * while the track samples it every step. That difference is the whole reason
 * `poiBearing` prefers this — on the corpus trip, POI-to-POI aims the summit
 * photos at a car park visited three days later, because between those two
 * POIs the trip genuinely did go that way. The track knows the trail turns.
 *
 * Reads the nearest vertex and then looks `ROUTE_TANGENT_METRES` further along
 * the same line, falling back to the stretch behind it at the end of a track.
 * An out-and-back doubles over itself, so a POI beside the overlap may attach
 * to either pass — worth knowing when a heading looks reversed.
 */
export function routeBearing(
  route: MapMultiLineString | null | undefined,
  here: [number, number],
): number | null {
  if (!route?.coordinates?.length) return null

  let best: { line: [number, number][]; index: number; metres: number } | null = null
  for (const line of route.coordinates) {
    for (let i = 0; i < line.length; i++) {
      const v = line[i]
      if (!v) continue
      const d = metresBetween(here, v)
      if (!best || d < best.metres) best = { line, index: i, metres: d }
    }
  }
  if (!best || best.metres > ROUTE_ATTACH_METRES) return null

  const { line, index } = best
  const at = line[index]!

  // Forward first — the direction of travel is the direction the track runs.
  let walked = 0
  for (let i = index + 1; i < line.length; i++) {
    walked += metresBetween(line[i - 1]!, line[i]!)
    if (walked >= ROUTE_TANGENT_METRES) {
      return wrap360(bearing(point(at), point(line[i]!)))
    }
  }
  // At the end of the track, read the stretch that led into it instead.
  walked = 0
  for (let i = index - 1; i >= 0; i--) {
    walked += metresBetween(line[i + 1]!, line[i]!)
    if (walked >= ROUTE_TANGENT_METRES) {
      return wrap360(bearing(point(line[i]!), point(at)))
    }
  }
  return null
}

/**
 * Which way to face, best answer first.
 *
 * 1. The recorded track, where the trip has one and this POI sits on it — see
 *    `routeBearing`. Every POI-based rule below is an approximation of this
 *    one from a much sparser sample.
 * 2. Where the trip goes next — the first POI at least `LOOKAHEAD_METRES`
 *    away, so the camera arrives looking onward down the actual route.
 * 3. The same rule with the distance gate dropped, for a trip that never
 *    leaves one hillside. Noisy, but a noisy heading beats no heading when
 *    twenty metres is genuinely all the trip has.
 * 4. `poi.dir` — where the photographer pointed the camera, according to the
 *    phone. **Last**, not first, and that ordering is the finding of a long
 *    afternoon: on the Picacho Peak corpus the Pixel 8a's magnetometer wrote
 *    headings up to 125 degrees wrong, and two photos of the same massif taken
 *    from fifty metres apart disagree with each other by 220 degrees. An
 *    uncalibrated compass does not know it is uncalibrated, so the tag arrives
 *    confidently wrong — which is worse than absent, because it outranks every
 *    fallback that would have been right. Kept only because a trip tight
 *    enough to exhaust both rules above has nothing else left, and because a
 *    hand-corrected `dir` is still the best fact there is.
 * 5. `null` — hold the current bearing. Turning to an arbitrary heading would
 *    be worse than not turning, because a turn reads as meaning something.
 */
export function poiBearing(
  pois: MapPoi[],
  index: number,
  route?: MapMultiLineString | null,
): number | null {
  const poi = pois[index]
  if (!poi) return null

  const here = coords(poi)
  if (here) {
    const onward = routeBearing(route, here) ??
      travelBearing(pois, index, here, LOOKAHEAD_METRES) ??
      travelBearing(pois, index, here, 0)
    if (onward !== null) return onward
  }

  const own = num(poi.dir)
  return own === null ? null : wrap360(own)
}

// -----------------------------------------------------------------------------
// The sun
// -----------------------------------------------------------------------------

/**
 * The sun's altitude above the horizon, in degrees, at an instant and a place.
 *
 * The usual low-precision solar position algorithm (NOAA's, good to about a
 * minute of arc — far past what picking one of four light presets needs).
 *
 * Note what this does *not* need: the photo's timezone. Solar position is a
 * function of the UTC instant and the point on the globe, so `took` and the
 * coordinates are sufficient and `OffsetTimeOriginal` never has to cross to the
 * client. That also makes it correct at any latitude and season, which is the
 * thing bucketing the local hour gets wrong — 18:00 in a Norwegian summer is
 * broad daylight, and an hour rule calls it dusk.
 */
export function solarAltitude(at: Date, lat: number, lng: number): number {
  // Days since the J2000.0 epoch.
  const d = at.getTime() / 86_400_000 - 10_957.5

  // Mean anomaly, then the ecliptic longitude of the sun.
  const g = (357.529 + 0.98560028 * d) * DEG
  const q = (280.459 + 0.98564736 * d) * DEG
  const lambda = q + (1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * DEG

  // Obliquity of the ecliptic, then declination and right ascension.
  const eps = (23.439 - 0.00000036 * d) * DEG
  const declination = Math.asin(Math.sin(eps) * Math.sin(lambda))
  const rightAscension = Math.atan2(
    Math.cos(eps) * Math.sin(lambda),
    Math.cos(lambda),
  )

  // Greenwich mean sidereal time, in degrees, then the local hour angle.
  const gmst = 280.46061837 + 360.98564736629 * d
  const hourAngle = (gmst + lng) * DEG - rightAscension

  const latRad = lat * DEG
  const sinAltitude = Math.sin(latRad) * Math.sin(declination) +
    Math.cos(latRad) * Math.cos(declination) * Math.cos(hourAngle)
  return Math.asin(Math.max(-1, Math.min(1, sinAltitude))) / DEG
}

/**
 * True when the sun is climbing — i.e. it is morning here.
 *
 * Separates dawn from dusk, which sit at the same solar altitude and look
 * nothing alike. Sampled rather than derived from the hour angle because the
 * hour angle's sign flips at local noon, which is not the same instant as UTC
 * noon and is exactly the kind of off-by-a-timezone this module is avoiding.
 */
function sunIsRising(at: Date, lat: number, lng: number): boolean {
  const later = new Date(at.getTime() + 600_000)
  return solarAltitude(later, lat, lng) > solarAltitude(at, lat, lng)
}

/** Above this altitude the light is plain daylight. */
export const DAY_ALTITUDE = 6

/** Below this altitude there is no usable light left. */
export const NIGHT_ALTITUDE = -6

/**
 * The light preset for a moment and a place.
 *
 * The band between the two thresholds is golden hour plus civil twilight — the
 * window Mapbox's `dawn` and `dusk` presets are drawn for.
 */
export function lightPresetFor(at: Date, lat: number, lng: number): LightPreset {
  const altitude = solarAltitude(at, lat, lng)
  if (altitude > DAY_ALTITUDE) return "day"
  if (altitude < NIGHT_ALTITUDE) return "night"
  return sunIsRising(at, lat, lng) ? "dawn" : "dusk"
}

/**
 * The light a POI was photographed in, or `null` when it cannot be known.
 *
 * Prefers the EXIF shutter instant over `took`, because `took` is editable and
 * an owner fixing a photo's *date* is not thinking about the time of day they
 * set along with it — see `lib/pois/cam-hints.ts`.
 */
export function poiLight(poi: MapPoi): LightPreset | null {
  const when = poi.cam?.at ?? poi.took
  if (!when) return null
  const at = new Date(when)
  if (Number.isNaN(at.getTime())) return null
  const here = coords(poi)
  if (!here) return null
  return lightPresetFor(at, here[1], here[0])
}

/**
 * The light a whole trip is shown in, before any one POI is in focus.
 *
 * The page load, the Lead-in, and a POI whose own time is unknowable all need
 * *some* light, and `day` — what this used to hardcode — is a claim about the
 * trip rather than the absence of one. A trip photographed entirely at dusk
 * opened in flat noon and then jumped when its first POI was focused.
 *
 * The rule is the **first POI that can be dated**, in playback order. Not the
 * modal preset, which was the obvious alternative and is wrong for the case
 * that matters: a trip that starts before sunrise and runs into the morning is
 * mostly `day` by count, so the mode opens it in flat daylight and then throws
 * the dawn away at the very moment the viewer is looking at the trip's first
 * photograph. The opening frame of a trip is the one the first POI establishes,
 * so that is the POI the map should already agree with. A trip that never
 * leaves one preset — which is every trip in the local corpus — resolves the
 * same either way.
 *
 * "That can be dated" is doing real work: `poiLight` returns `null` for a POI
 * with no shutter instant or no coordinates, and an undatable POI at position 0
 * should not condemn the trip to `day`. The scan continues to the first POI
 * that actually answers.
 *
 * Measurement is what makes this worth doing at all. Flipping `lightPreset`
 * costs a 190-550ms frozen frame and does not amortise, while re-asserting the
 * value the map already holds is free. Opening on the trip's own light means
 * the per-POI lighting below usually has nothing left to change: across the
 * whole local corpus — 41 located POIs over 6 trips — there are **zero**
 * adjacent light transitions, so the flip happens once during style load and
 * never again while the viewer browses. See
 * `wiki/findings/mapbox-config-repaint-loop.md`.
 */
export function tripLight(pois: MapPoi[]): LightPreset {
  for (const poi of pois) {
    const light = poiLight(poi)
    if (light) return light
  }
  return "day"
}

// -----------------------------------------------------------------------------

/**
 * The whole camera for one POI.
 *
 * Takes the whole playback sequence rather than the two neighbours, because
 * the bearing rule looks past the neighbours to the first POI far enough away
 * to describe the route — see `LOOKAHEAD_METRES`.
 */
export function poiCamera(
  pois: MapPoi[],
  index: number,
  route?: MapMultiLineString | null,
): PoiCamera {
  const poi = pois[index]
  if (!poi) return { bearing: null, pitch: PLAYER_PITCH, zoom: BASE_ZOOM, light: null }
  return {
    bearing: poiBearing(pois, index, route),
    pitch: PLAYER_PITCH,
    zoom: zoomForFocalLength(poi.cam?.f35),
    light: poiLight(poi),
  }
}
